/**
 * Pack-opening SFX. Prefers Pixabay clips in /sfx, else a short WAV synth
 * so it still works when the folder is empty.
 */

import { SampleBank } from './SampleBank'
import { synth } from './SoftSynth'

export type PackSfxKind =
  | 'whoosh'
  | 'tear'
  | 'rip'
  | 'burst'
  | 'slide'
  | 'common'
  | 'rare'
  | 'super'
  | 'ultra'
  | 'secret'
  | 'summary'
  | 'skip'
  | 'stow'
  | 'lid'

const MUTE_KEY = 'yugioh.packSfxMuted'
const SAMPLE_RATE = 44100

const PACK_SAMPLES: readonly PackSfxKind[] = [
  'whoosh',
  'tear',
  'rip',
  'burst',
  'slide',
  'common',
  'rare',
  'super',
  'ultra',
  'secret',
  'summary',
  'skip',
  'stow',
  'lid',
]

const writeStr = (view: DataView, offset: number, text: string) => {
  for (let i = 0; i < text.length; i++) view.setUint8(offset + i, text.charCodeAt(i))
}

const toWav = (samples: Float32Array): Blob => {
  const n = samples.length
  const bytes = new ArrayBuffer(44 + n * 2)
  const view = new DataView(bytes)
  writeStr(view, 0, 'RIFF')
  view.setUint32(4, 36 + n * 2, true)
  writeStr(view, 8, 'WAVE')
  writeStr(view, 12, 'fmt ')
  view.setUint32(16, 16, true)
  view.setUint16(20, 1, true)
  view.setUint16(22, 1, true)
  view.setUint32(24, SAMPLE_RATE, true)
  view.setUint32(28, SAMPLE_RATE * 2, true)
  view.setUint16(32, 2, true)
  view.setUint16(34, 16, true)
  writeStr(view, 36, 'data')
  view.setUint32(40, n * 2, true)
  let offset = 44
  let peak = 0.001
  for (let i = 0; i < n; i++) peak = Math.max(peak, Math.abs(samples[i] ?? 0))
  const norm = 0.82 / peak
  for (let i = 0; i < n; i++) {
    const clipped = Math.max(-1, Math.min(1, (samples[i] ?? 0) * norm))
    view.setInt16(offset, clipped * 32767, true)
    offset += 2
  }
  return new Blob([bytes], { type: 'audio/wav' })
}

const mixInto = (out: Float32Array, part: Float32Array, at = 0): void => {
  const start = Math.floor(at * SAMPLE_RATE)
  for (let i = 0; i < part.length; i++) {
    const idx = start + i
    if (idx < out.length) out[idx] += part[i] ?? 0
  }
}

const alloc = (seconds: number): Float32Array =>
  new Float32Array(Math.floor(SAMPLE_RATE * seconds))

type Wave = 'sine' | 'square' | 'saw' | 'tri'

const sampleWave = (kind: Wave, phase: number): number => {
  const ph = phase - Math.floor(phase)
  if (kind === 'sine') return Math.sin(2 * Math.PI * phase)
  if (kind === 'square') return ph < 0.22 ? 1 : -0.35
  if (kind === 'saw') return 2 * ph - 1
  return 4 * Math.abs(ph - 0.5) - 1
}

/** Damped oscillator. Square/saw stay short so they read sci-fi, not 8-bit. */
const toneW = (
  kind: Wave,
  freq: number,
  dur: number,
  vol: number,
  decay = 10,
): Float32Array => {
  const n = Math.floor(SAMPLE_RATE * dur)
  const out = new Float32Array(n)
  const attack = Math.min(0.004 * SAMPLE_RATE, n / 10)
  for (let i = 0; i < n; i++) {
    const a = i < attack ? i / attack : 1
    const env = a * Math.exp((-i * decay) / SAMPLE_RATE) * vol
    out[i] = sampleWave(kind, (freq * i) / SAMPLE_RATE) * env
  }
  return out
}

const glide = (
  kind: Wave,
  from: number,
  to: number,
  dur: number,
  vol: number,
): Float32Array => {
  const n = Math.floor(SAMPLE_RATE * dur)
  const out = new Float32Array(n)
  let phase = 0
  for (let i = 0; i < n; i++) {
    const t = i / n
    const freq = from * (to / Math.max(1, from)) ** t
    phase += freq / SAMPLE_RATE
    const env = Math.sin(Math.PI * t) ** 0.55 * vol
    out[i] = sampleWave(kind, phase) * env
  }
  return out
}

const pink = (dur: number, vol: number): Float32Array => {
  const n = Math.floor(SAMPLE_RATE * dur)
  const out = new Float32Array(n)
  let b0 = 0
  let b1 = 0
  let b2 = 0
  const attack = Math.min(0.008 * SAMPLE_RATE, n / 8)
  const release = Math.min(0.1 * SAMPLE_RATE, n / 2)
  for (let i = 0; i < n; i++) {
    const white = Math.random() * 2 - 1
    b0 = 0.99765 * b0 + white * 0.099046
    b1 = 0.963 * b1 + white * 0.2965164
    b2 = 0.57 * b2 + white * 1.052691
    const a = i < attack ? i / attack : 1
    const r = i > n - release ? (n - i) / release : 1
    out[i] = (b0 + b1 + b2 + white * 0.18) * a * r * vol
  }
  return out
}

const white = (dur: number, vol: number): Float32Array => {
  const n = Math.floor(SAMPLE_RATE * dur)
  const out = new Float32Array(n)
  const attack = Math.min(0.003 * SAMPLE_RATE, n / 6)
  const release = Math.min(0.06 * SAMPLE_RATE, n / 2)
  for (let i = 0; i < n; i++) {
    const a = i < attack ? i / attack : 1
    const r = i > n - release ? (n - i) / release : 1
    out[i] = (Math.random() * 2 - 1) * a * r * vol
  }
  return out
}

/** One-pole high-pass so rumble does not turn into a slot-machine thud. */
const highpass = (samples: Float32Array, cutoffHz: number): Float32Array => {
  const out = new Float32Array(samples.length)
  const rc = 1 / (2 * Math.PI * cutoffHz)
  const dt = 1 / SAMPLE_RATE
  const a = rc / (rc + dt)
  let prevX = 0
  let prevY = 0
  for (let i = 0; i < samples.length; i++) {
    const x = samples[i] ?? 0
    const y = a * (prevY + x - prevX)
    out[i] = y
    prevX = x
    prevY = y
  }
  return out
}

const lowpass = (samples: Float32Array, cutoffHz: number): Float32Array => {
  const out = new Float32Array(samples.length)
  const rc = 1 / (2 * Math.PI * cutoffHz)
  const dt = 1 / SAMPLE_RATE
  const a = dt / (rc + dt)
  let prev = 0
  for (let i = 0; i < samples.length; i++) {
    prev += a * ((samples[i] ?? 0) - prev)
    out[i] = prev
  }
  return out
}

/** Band-limited air / whoosh used as the backbone of modern UI motion. */
const air = (dur: number, vol: number, fromHz: number, toHz: number): Float32Array => {
  const raw = pink(dur, vol)
  const n = raw.length
  const out = new Float32Array(n)
  let lp = 0
  let hp = 0
  let prev = 0
  for (let i = 0; i < n; i++) {
    const t = i / n
    const cutoff = fromHz + (toHz - fromHz) * t
    const lpA = 1 - Math.exp((-2 * Math.PI * cutoff) / SAMPLE_RATE)
    const hpA = Math.exp((-2 * Math.PI * (cutoff * 0.35)) / SAMPLE_RATE)
    lp += lpA * ((raw[i] ?? 0) - lp)
    hp = hpA * (hp + lp - prev)
    prev = lp
    const env = Math.sin(Math.PI * t) ** 0.7
    out[i] = hp * env
  }
  return out
}

const RECIPES: Record<PackSfxKind, () => Float32Array> = {
  whoosh: () =>
    lowpass(glide('saw', 2100, 280, 0.22, 0.22), 2400),
  tear: () => {
    const out = alloc(0.13)
    mixInto(out, toneW('square', 1850, 0.04, 0.2, 40))
    mixInto(out, toneW('square', 2400, 0.04, 0.16, 40), 0.035)
    mixInto(out, toneW('square', 1500, 0.05, 0.14, 36), 0.07)
    mixInto(out, highpass(white(0.08, 0.18), 2200), 0.02)
    return out
  },
  rip: () => {
    const out = alloc(0.38)
    mixInto(out, glide('saw', 1600, 90, 0.3, 0.24))
    mixInto(out, highpass(white(0.12, 0.28), 1800), 0.04)
    mixInto(out, toneW('square', 70, 0.16, 0.2, 12), 0.06)
    return lowpass(out, 3200)
  },
  burst: () => {
    const out = alloc(0.4)
    mixInto(out, toneW('sine', 55, 0.18, 0.28, 10))
    mixInto(out, glide('saw', 180, 1400, 0.2, 0.16), 0.03)
    mixInto(out, toneW('tri', 1680, 0.12, 0.14, 18), 0.1)
    mixInto(out, highpass(white(0.07, 0.16), 5000), 0.08)
    return out
  },
  slide: () => {
    const out = alloc(0.18)
    mixInto(out, glide('tri', 320, 620, 0.14, 0.18))
    mixInto(out, toneW('square', 110, 0.08, 0.08, 22), 0.02)
    return out
  },
  common: () => {
    const out = alloc(0.2)
    mixInto(out, toneW('square', 720, 0.07, 0.16, 28))
    mixInto(out, toneW('square', 480, 0.08, 0.14, 24), 0.07)
    return lowpass(out, 2600)
  },
  rare: () => {
    const out = alloc(0.42)
    mixInto(out, glide('saw', 240, 960, 0.22, 0.14))
    mixInto(out, toneW('tri', 1480, 0.16, 0.16, 16), 0.14)
    mixInto(out, highpass(white(0.06, 0.1), 6000), 0.16)
    return out
  },
  super: () => {
    const out = alloc(0.55)
    mixInto(out, glide('saw', 140, 880, 0.32, 0.16))
    mixInto(out, toneW('square', 660, 0.14, 0.14, 14), 0.2)
    mixInto(out, toneW('sine', 48, 0.2, 0.2, 10), 0.04)
    return lowpass(out, 2800)
  },
  ultra: () => {
    const out = alloc(0.78)
    mixInto(out, toneW('sine', 38, 0.32, 0.32, 7))
    mixInto(out, glide('saw', 90, 720, 0.4, 0.14), 0.06)
    mixInto(out, toneW('tri', 1980, 0.2, 0.12, 12), 0.28)
    mixInto(out, highpass(white(0.1, 0.12), 7000), 0.22)
    return out
  },
  secret: () => {
    const out = alloc(1.05)
    mixInto(out, toneW('sine', 64, 0.7, 0.22, 4))
    mixInto(out, toneW('saw', 111, 0.55, 0.08, 5), 0.05)
    mixInto(out, glide('tri', 700, 1700, 0.45, 0.1), 0.12)
    mixInto(out, toneW('square', 1400, 0.12, 0.08, 18), 0.38)
    mixInto(out, toneW('square', 980, 0.14, 0.07, 16), 0.5)
    mixInto(out, highpass(white(0.16, 0.1), 5500), 0.2)
    return lowpass(out, 3600)
  },
  summary: () => {
    const out = alloc(0.48)
    mixInto(out, toneW('square', 840, 0.08, 0.14, 22))
    mixInto(out, toneW('square', 560, 0.09, 0.13, 20), 0.1)
    mixInto(out, toneW('square', 360, 0.12, 0.12, 16), 0.2)
    mixInto(out, toneW('sine', 70, 0.18, 0.14, 10), 0.08)
    return lowpass(out, 2200)
  },
  skip: () => toneW('square', 1900, 0.05, 0.14, 50),
  stow: () => {
    const out = alloc(0.16)
    mixInto(out, glide('tri', 420, 180, 0.1, 0.16))
    mixInto(out, toneW('square', 90, 0.07, 0.12, 24), 0.03)
    return out
  },
  lid: () => {
    const out = alloc(0.3)
    mixInto(out, air(0.16, 0.35, 1800, 400))
    mixInto(out, toneW('square', 68, 0.1, 0.22, 18), 0.08)
    mixInto(out, toneW('tri', 240, 0.08, 0.1, 20), 0.1)
    return out
  },
}

class PackSfxController {
  private muted = false
  private readonly clips = new Map<PackSfxKind, AudioBuffer>()
  private readonly pending = new Set<PackSfxKind>()

  constructor() {
    try {
      this.muted = localStorage.getItem(MUTE_KEY) === '1'
    } catch {
      this.muted = false
    }
    SampleBank.preload(PACK_SAMPLES.map((kind) => `pack-${kind}`))
  }

  isMuted(): boolean {
    return this.muted
  }

  setMuted(value: boolean): void {
    this.muted = value
    try {
      localStorage.setItem(MUTE_KEY, value ? '1' : '0')
    } catch {
      /* ignore */
    }
  }

  toggle(): boolean {
    this.setMuted(!this.muted)
    return this.muted
  }

  play(kind: PackSfxKind): void {
    if (this.muted) return
    if (SampleBank.play(`pack-${kind}`)) return
    const ctx = synth.unlock()
    if (!ctx) return
    const cached = this.clips.get(kind)
    if (cached) {
      this.shot(ctx, cached)
      return
    }
    if (this.pending.has(kind)) return
    this.pending.add(kind)
    void this.synthClip(ctx, kind).finally(() => this.pending.delete(kind))
  }

  /**
   * Generated fallback. Web Audio, same reason as BGM: an <audio> element
   * takes exclusive playback and pauses other apps.
   */
  private shot(ctx: AudioContext, buffer: AudioBuffer): void {
    const source = ctx.createBufferSource()
    source.buffer = buffer
    const gain = ctx.createGain()
    gain.gain.value = 0.82
    source.connect(gain)
    gain.connect(ctx.destination)
    source.start()
  }

  private async synthClip(ctx: AudioContext, kind: PackSfxKind): Promise<void> {
    try {
      const raw = await toWav(RECIPES[kind]()).arrayBuffer()
      const buffer = await ctx.decodeAudioData(raw.slice(0))
      this.clips.set(kind, buffer)
      this.shot(ctx, buffer)
    } catch {
      /* next click retries */
    }
  }
}

export const PackSfx = new PackSfxController()
